import React, { useMemo } from 'react';
import { Stack } from '@mantine/core';
import {
    BaseCommentWithMentionsAndUser,
    MentionWithUser,
} from '@/common/types/databaseTypes';
import { CommentHeader } from './CommentHeader';
import { ReplySection } from './ReplySection';
import { CommentContent } from './CommentContent';
import { UserBase } from '@tickitacka/shared';

interface CommentItemProps {
    comment: BaseCommentWithMentionsAndUser;
    isReply?: boolean;
    editingCommentId: string | null;
    replyingToId: string | null;
    onReplyClick: (commentId: string) => void;
    onEditClick: (comment: BaseCommentWithMentionsAndUser) => void;
    onDeleteClick: (comment: BaseCommentWithMentionsAndUser) => void;
    renderEditForm: (
        comment: BaseCommentWithMentionsAndUser
    ) => React.ReactNode;
    renderReplyForm: (
        comment: BaseCommentWithMentionsAndUser
    ) => React.ReactNode;
    isCollapsed: boolean;
    onToggleReplies: (commentId: string) => void;
}

export const CommentItem: React.FC<CommentItemProps> = ({
    comment,
    isReply = false,
    editingCommentId,
    replyingToId,
    onReplyClick,
    onEditClick,
    onDeleteClick,
    renderEditForm,
    renderReplyForm,
    isCollapsed,
    onToggleReplies,
}: CommentItemProps) => {
    // 멘션된 사용자 맵 생성 - 메모이제이션
    const mentionedUsersMap = useMemo(() => {
        const map: Record<string, UserBase> = {};
        if (comment.mentions && Array.isArray(comment.mentions)) {
            comment.mentions.forEach((mention: MentionWithUser) => {
                if (mention.mention_user) {
                    map[mention.mention_user.id] = mention.mention_user;
                }
            });
        }
        return map;
    }, [comment.mentions]);

    return (
        <Stack gap={4} ml={isReply ? 16 : 0}>
            <CommentHeader
                comment={comment}
                isReply={isReply}
                onReplyClick={() => onReplyClick(comment.id)}
                onEditClick={() => onEditClick(comment)}
                onDeleteClick={() => onDeleteClick(comment)}
                showReplyButton={!isReply}
            />

            {editingCommentId === comment.id ? (
                renderEditForm(comment)
            ) : (
                <CommentContent
                    content={comment.content}
                    mentionedUsers={mentionedUsersMap}
                />
            )}

            {/* 답글 목록 */}
            {comment.children && comment.children.length > 0 && (
                <ReplySection
                    comment={comment}
                    // isCollapsed={isCollapsed(comment.id)}
                    isCollapsed={isCollapsed}
                    onToggle={() => onToggleReplies(comment.id)}
                >
                    {comment.children.map(reply => (
                        <CommentItem
                            key={reply.id}
                            comment={reply}
                            isReply={true}
                            editingCommentId={editingCommentId}
                            replyingToId={replyingToId}
                            onReplyClick={onReplyClick}
                            onEditClick={onEditClick}
                            onDeleteClick={onDeleteClick}
                            renderEditForm={renderEditForm}
                            renderReplyForm={renderReplyForm}
                            isCollapsed={isCollapsed}
                            onToggleReplies={onToggleReplies}
                        />
                    ))}
                </ReplySection>
            )}

            {/* 답글 작성 폼 */}
            {replyingToId === comment.id && renderReplyForm(comment)}
        </Stack>
    );
};
